import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { getLocaleKey } from "@faire/retailer-visitor-shared/serialized-data/getLocaleKey";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import {
  DEPRECATED_backwardsCompatibleValues,
  StrictLocalizeFunction,
} from "@faire/web--source/common/localization";
import { getCountryDisplayNameFromSessionCountry } from "@faire/web--source/common/localization/getCountryDisplayNameFromSessionCountry";
import { Language } from "@faire/web-api--source/indigofair/languages/Language";

export const getMetaTitle = (
  {
    productName,
    isFaireDirectActiveInLoggedOut,
  }: {
    productName: string | undefined;
    isFaireDirectActiveInLoggedOut: boolean;
  },
  strictLocalize: StrictLocalizeFunction,
  showForLoggedInRetailerOrBrandPreview = true
): string | null => {
  if (!productName) {
    return null;
  }

  const countryDisplayName = getCountryDisplayNameFromSessionCountry(
    getSessionCountry(),
    strictLocalize
  );

  const shouldShowMetaTitle = shouldShowSEOMetaText({
    showForLoggedInRetailerOrBrandPreview,
    isFaireDirectActiveInLoggedOut,
  });

  if (shouldShowMetaTitle) {
    if (shouldNotPrependWholesaleWord(productName)) {
      return countryDisplayName
        ? strictLocalize(
            {
              defaultMessage: "{ProductName} for your store - Faire {country}",
              description: {
                text: "Meta-title for logged-out product page (for SEO purposes). Please ensure we maintain the hyphen ('-') in '- Faire...",
              },
            },
            DEPRECATED_backwardsCompatibleValues({
              ProductName: productName,
              country: countryDisplayName,
            })
          )
        : strictLocalize(
            {
              defaultMessage: "{ProductName} for your store - Faire",
              description: {
                text: "Meta-title for logged-out product page (for SEO purposes). Please ensure we maintain the hyphen ('-') in '- Faire...",
              },
            },
            { ProductName: productName }
          );
    }
    return countryDisplayName
      ? strictLocalize(
          {
            defaultMessage:
              "Wholesale {ProductName} for your store - Faire {country}",
            description: {
              text: "Meta-title for logged-out product page (for SEO purposes). Please ensure we maintain the hyphen ('-') in '- Faire...",
            },
          },
          DEPRECATED_backwardsCompatibleValues({
            ProductName: productName,
            country: countryDisplayName,
          })
        )
      : strictLocalize(
          {
            defaultMessage: "Wholesale {ProductName} for your store - Faire",
            description: {
              text: "Meta-title for logged-out product page (for SEO purposes). Please ensure we maintain the hyphen ('-') in '- Faire...",
            },
          },
          { ProductName: productName }
        );
  }

  return `${productName} - Faire`;
};

export const getMetaDescription = (
  {
    productName,
    brandCompanyName,
    productDescription,
    isFaireDirectActiveInLoggedOut,
  }: {
    productName: string | undefined;
    brandCompanyName: string | undefined;
    productDescription: string | undefined;
    isFaireDirectActiveInLoggedOut: boolean;
  },
  strictLocalize: StrictLocalizeFunction
): string | null => {
  if (!productName || !brandCompanyName || !productDescription) {
    return null;
  }

  const shouldShowMetaDescription = shouldShowSEOMetaText({
    showForLoggedInRetailerOrBrandPreview: true,
    isFaireDirectActiveInLoggedOut,
  });

  if (shouldShowMetaDescription) {
    if (shouldNotPrependWholesaleWord(productName)) {
      return strictLocalize(
        {
          defaultMessage:
            "Shop {ProductName} from {brandCompanyName} and more for your store on Faire. Stock up on unique brands and products, with free returns and low order minimums.",
          description: {
            text: "Meta-description for logged-out brand page (for SEO purposes, not apparently visible in the user interface)",
          },
        },
        {
          ProductName: productName,
          brandCompanyName,
        }
      );
    }

    return strictLocalize(
      {
        defaultMessage:
          "Shop wholesale {ProductName} from {brandCompanyName} and more for your store on Faire. Stock up on unique brands and products, with free returns and low order minimums.",
        description: {
          text: "Meta-description for logged-out brand page (for SEO purposes, not apparently visible in the user interface)",
        },
      },
      {
        ProductName: productName,
        brandCompanyName,
      }
    );
  }

  return strictLocalize(
    {
      defaultMessage: "Buy wholesale with free returns. {description}",
      description: {
        text: "Title for website",
      },
    },
    DEPRECATED_backwardsCompatibleValues({
      description: productDescription,
    })
  );
};

export const getProductHeaderName = (
  {
    productName,
    isFaireDirectActiveInLoggedOut,
  }: {
    productName: string | undefined;
    isFaireDirectActiveInLoggedOut: boolean;
  },
  strictLocalize: StrictLocalizeFunction
): string | null => {
  if (!productName) {
    return null;
  }

  const shouldShowHeader = shouldShowSEOMetaText({
    hideForFaireDirect: true,
    isFaireDirectActiveInLoggedOut,
  });

  if (!shouldShowHeader || shouldNotPrependWholesaleWord(productName)) {
    return productName;
  }

  if (shouldUpperWholesaleWord(productName)) {
    return strictLocalize(
      {
        defaultMessage: "WHOLESALE {ProductName}",
        description: {
          text: "Header of product page (PDP). This is a variant to the usual header where we upper case 'WHOLESALE' if the product name is also mostly upper case",
          img: "https://cdn.faire.com/fastly/d320cd9dd93654b68057266330c151632b0e7119281ba0aefc345c546fd07bfa.png",
        },
      },
      { ProductName: productName }
    );
  }

  return strictLocalize(
    {
      defaultMessage: "Wholesale {ProductName}",
      description: {
        text: "Header of product page (PDP)",
        img: "https://cdn.faire.com/fastly/a8e9a5303b38dd499cd1dd4dd981687671c65e290b6574300ec418ec0ff9c04d.png",
      },
    },
    { ProductName: productName }
  );
};

export const getProductSubheaderName = (
  {
    brandCompanyName,
    isFaireDirectActiveInLoggedOut,
  }: {
    brandCompanyName: string | undefined;
    isFaireDirectActiveInLoggedOut: boolean;
  },
  strictLocalize: StrictLocalizeFunction
): string | null => {
  const shouldShowSubheader = shouldShowSEOMetaText({
    hideForFaireDirect: true,
    isFaireDirectActiveInLoggedOut,
  });

  if (!brandCompanyName || !shouldShowSubheader) {
    return null;
  }

  return strictLocalize(
    {
      defaultMessage:
        "With Faire, shop unique wholesale products for your store from brands like {brandCompanyName} and more.",
      description: {
        text: "Subheader text of product page (PDP)",
        img: "https://cdn.faire.com/fastly/412cbd12e30b196e057e91ccf344e14263a55320c52e14c6fb160d882aee715a.png",
      },
    },
    { brandCompanyName }
  );
};

const shouldShowSEOMetaText = (options: {
  isFaireDirectActiveInLoggedOut: boolean;
  hideForFaireDirect?: boolean;
  showForLoggedInRetailerOrBrandPreview?: boolean;
}): boolean => {
  const hideForFaireDirect = options.hideForFaireDirect ?? false;
  const showForLoggedInRetailerOrBrandPreview =
    options.showForLoggedInRetailerOrBrandPreview ?? false;

  return (
    (showForLoggedInRetailerOrBrandPreview ||
      !isLoggedInRetailerOrBrandPreview()) &&
    !(hideForFaireDirect && options?.isFaireDirectActiveInLoggedOut)
  );
};

const wholesaleKeywordsMap = new Map<Language, RegExp>([
  [Language.ENG, /\b(wholesale|whole sale|whole-sale|wholesal|bulk)\b/gi],
  [Language.FRA, /\b(en gros|en vente B2B|de gros|en vrac)\b/gi],
  [
    Language.ITA,
    /\b(in vendita B2B|in stock|in blocco|all’ingrosso|all'ingrosso)\b/gi,
  ],
  [
    Language.SPA,
    /\b(al por mayor|mayorista|en masa|a granel|en lote|por lotes)\b/gi,
  ],
  // We don't want word boundaries in DEU because they form compound words (ie. Großhandelssocken).
  [Language.DEU, /(Massen|Sammel|Großhandel|Mengen|Grosshandel)/gi],
]);

const shouldNotPrependWholesaleWord = (name: string): boolean => {
  const language = (getLocaleKey()?.language ?? Language.ENG) as Language;

  if (!wholesaleKeywordsMap.has(language)) {
    return true;
  } else {
    return name.search(wholesaleKeywordsMap.get(language) as RegExp) !== -1;
  }
};

// We want to check if the number of capital letters in a string (compared to all letters
// in the string) exceeds a threshold to decide whether we should upper case "wholesale".
const shouldUpperWholesaleWord = (name: string): boolean => {
  const THRESHOLD_FOR_UPPER_CASE = 0.7;

  let numberOfUpperCaseCharactersInName = 0;
  let numberOfNaNInName = 0;

  for (const char of name) {
    if (isNaN(Number(char))) {
      numberOfNaNInName++;

      if (char === char.toUpperCase()) {
        numberOfUpperCaseCharactersInName++;
      }
    }
  }

  return (
    numberOfUpperCaseCharactersInName / numberOfNaNInName >
    THRESHOLD_FOR_UPPER_CASE
  );
};
